//
// # useLayerInfo Hook
//
// Watches the active editor and extracts layer information for layout content.
// Returns grouped PDK layers with visibility state and toggle callbacks.
//
// Re-computes whenever the active ident changes or content is reloaded.
//

import { useCallback, useEffect, useState } from "react";
import type { schema } from "GenAlphaSchema";
import {
  getLayersToDisplay,
  groupLayersByCategory,
  type LayoutContent,
  type LayoutDisplayOptions,
  type LayoutSelectability,
  type Editor,
} from "EditorCore";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";

export interface LayerInfoState {
  // Grouped layers by category (metal, via, etc.), or null when not a layout
  readonly layersByCategory: Map<string, Array<schema.pdk.PDKLayer>> | null;
  // All layer numbers currently displayed (for solo mode)
  readonly allLayerNumbers: Array<number>;
  // PDK name for display
  readonly pdkName: string | null;
  // Stack name for display
  readonly stackName: string | null;
  // Set of currently hidden layer numbers
  readonly hiddenLayers: ReadonlySet<number>;
  // Layout display options (pins, labels, stack, blockages)
  readonly layoutDisplay: LayoutDisplayOptions | null;
  // Abstract view flag (lives on Editor, not LayoutContent)
  readonly showAbstractView: boolean;
  // Filter to show only layers with actual shapes (hide empty stack layers)
  readonly showUsedLayersOnly: boolean;
  // Per-category selectability flags, or null when not a layout
  readonly selectability: LayoutSelectability | null;
  // Callbacks
  readonly toggleLayer: (layerNumber: number) => void;
  readonly showOnlyLayer: (layerNumber: number) => void;
  readonly showAllLayers: () => void;
  readonly hideAllLayers: () => void;
  readonly toggleLayoutDisplay: (key: keyof LayoutDisplayOptions) => void;
  readonly toggleAbstractView: () => void;
  readonly toggleShowUsedLayersOnly: () => void;
  readonly toggleSelectability: (key: keyof LayoutSelectability) => void;
}

export function useLayerInfo(): LayerInfoState {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const activeIdent = snapshot.activeIdent;
  const contentVersion = snapshot.contentVersion;

  // Increment to force a re-render after in-place visibility mutations.
  const [, setRenderTick] = useState(0);

  // Filter to show only layers with actual shapes (hide empty stack layers)
  const [showUsedLayersOnly, setShowUsedLayersOnly] = useState(false);

  const [state, setState] = useState<{
    layersByCategory: Map<string, Array<schema.pdk.PDKLayer>> | null;
    allLayerNumbers: Array<number>;
    pdkName: string | null;
    stackName: string | null;
    // References for callbacks
    layoutContent: LayoutContent | null;
    editor: Editor | null;
  }>({
    layersByCategory: null,
    allLayerNumbers: [],
    pdkName: null,
    stackName: null,
    layoutContent: null,
    editor: null,
  });

  useEffect(() => {
    if (!activeIdent) {
      setState({
        layersByCategory: null,
        allLayerNumbers: [],
        pdkName: null,
        stackName: null,
        layoutContent: null,
        editor: null,
      });
      return;
    }

    const editor = workspace.getActiveEditor();
    if (!editor || editor.content.contentKind !== "layout") {
      setState({
        layersByCategory: null,
        allLayerNumbers: [],
        pdkName: null,
        stackName: null,
        layoutContent: null,
        editor: null,
      });
      return;
    }

    // Access the LayoutContent from the editor
    const layoutContent = editor.content as LayoutContent;
    const pdk = layoutContent.pdk;

    if (!pdk) {
      setState({
        layersByCategory: null,
        allLayerNumbers: [],
        pdkName: null,
        stackName: null,
        layoutContent,
        editor,
      });
      return;
    }

    const stack = layoutContent.data.stack;
    const layers = getLayersToDisplay(
      pdk,
      stack,
      layoutContent.render,
      showUsedLayersOnly
    );
    const grouped = groupLayersByCategory(layers);
    const allNums = layers.map((l) => l.number);

    setState({
      layersByCategory: grouped,
      allLayerNumbers: allNums,
      pdkName: pdk.name,
      stackName: stack?.name ?? null,
      layoutContent,
      editor,
    });
  }, [activeIdent, contentVersion, workspace, showUsedLayersOnly]);

  const toggleLayer = useCallback(
    (layerNumber: number) => {
      if (!state.layoutContent) return;
      state.layoutContent.toggleLayerVisibility(layerNumber);
      setRenderTick((v) => v + 1);
    },
    [state.layoutContent]
  );

  const showOnlyLayer = useCallback(
    (layerNumber: number) => {
      if (!state.layoutContent) return;
      state.layoutContent.showOnlyLayer(layerNumber, state.allLayerNumbers);
      setRenderTick((v) => v + 1);
    },
    [state.layoutContent, state.allLayerNumbers]
  );

  const showAllLayers = useCallback(() => {
    if (!state.layoutContent) return;
    state.layoutContent.showAllLayers();
    setRenderTick((v) => v + 1);
  }, [state.layoutContent]);

  const hideAllLayers = useCallback(() => {
    if (!state.layoutContent) return;
    state.layoutContent.hideAllLayers(state.allLayerNumbers);
    setRenderTick((v) => v + 1);
  }, [state.layoutContent, state.allLayerNumbers]);

  const toggleLayoutDisplay = useCallback(
    (key: keyof LayoutDisplayOptions) => {
      if (!state.layoutContent) return;
      state.layoutContent.toggleLayoutDisplay(key);
      setRenderTick((v) => v + 1);
    },
    [state.layoutContent]
  );

  const toggleAbstractView = useCallback(() => {
    if (!state.editor) return;
    state.editor.toggleAbstractView();
    setRenderTick((v) => v + 1);
  }, [state.editor]);

  const toggleShowUsedLayersOnly = useCallback(() => {
    setShowUsedLayersOnly((v) => !v);
  }, []);

  const toggleSelectability = useCallback(
    (key: keyof LayoutSelectability) => {
      if (!state.layoutContent) return;
      state.layoutContent.toggleSelectability(key);
      setRenderTick((v) => v + 1);
    },
    [state.layoutContent]
  );

  // Read hiddenLayers and display options from the layoutContent on each render
  // (including when renderTick changes after a toggle)
  const hiddenLayers: ReadonlySet<number> = state.layoutContent
    ? state.layoutContent.hiddenLayers
    : new Set();
  const layoutDisplay = state.layoutContent?.layoutDisplay ?? null;
  const showAbstractView = state.editor?.showAbstractView ?? false;
  const selectability = state.layoutContent?.selectability ?? null;

  return {
    layersByCategory: state.layersByCategory,
    allLayerNumbers: state.allLayerNumbers,
    pdkName: state.pdkName,
    stackName: state.stackName,
    hiddenLayers,
    layoutDisplay,
    showAbstractView,
    showUsedLayersOnly,
    selectability,
    toggleLayer,
    showOnlyLayer,
    showAllLayers,
    hideAllLayers,
    toggleLayoutDisplay,
    toggleAbstractView,
    toggleShowUsedLayersOnly,
    toggleSelectability,
  };
}
